import { Badge, Button, useTheme, useMediaQuery } from "@mui/material";
import { memo, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Flx from "../layout/Flx";

/**
 * EnhancedTabSwitcher - A flexible tab navigation component with badge support and routing
 *
 * Active tab appearance:
 *   - Background: theme.palette.secondary.activeTabBg  (translucent accent yellow)
 *   - Border:     theme.palette.primary.main            (brand blue)
 *
 * @param {Object} props
 * @param {string} props.value - Currently active tab value
 * @param {Function} props.onChange - Callback when tab changes (receives tab.value)
 * @param {Array} props.tabs - Array of tab configurations
 * @param {Object} props.sx - Additional styles for container
 * @param {string} props.orientation - "horizontal" or "vertical" (default: "horizontal")
 * @param {boolean} props.fullWidth - Make tabs take full width
 * @param {string} props.basePath - Base path for routing (e.g., "/profile")
 * @param {boolean} props.useRouting - Enable react-router navigation (default: false)
 *
 * Tab configuration shape:
 * {
 *   value: string (required) - Unique identifier for the tab
 *   label: string (required) - Desktop label
 *   mobileLabel: string (optional) - Mobile label, falls back to label
 *   icon: ReactNode (optional) - Icon component to display
 *   path: string (optional) - Route path (e.g., "/entities") - only used if useRouting=true
 *   badge: Object (optional) - Badge configuration
 *     {
 *       show: boolean - Whether to show badge
 *       variant: 'dot' | 'standard' - Badge variant
 *       color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
 *       content: string | number (optional) - Badge content for 'standard' variant
 *     }
 * }
 */
const EnhancedTabSwitcher = memo(
  ({
    value,
    onChange,
    tabs = [],
    sx = {},
    orientation = "horizontal",
    fullWidth = false,
    wrap = false,
    buttonColor,
    useRouting = false,
    basePath = "",
  }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isVertical = orientation === "vertical";

    // Active tab colors sourced from theme palette — no hardcoded values
    const activeTabBg = theme.palette.secondary.activeTabBg;
    const activeTabBorder = theme.palette.primary.main;

    const handleTabClick = useCallback(
      (tab) => {
        if (useRouting && tab.path) {
          const fullPath = basePath ? `${basePath}${tab.path}` : tab.path;
          navigate(fullPath);
        }
        if (onChange) {
          onChange(tab.value);
        }
      },
      [useRouting, basePath, navigate, onChange],
    );

    const buttonStyles = useMemo(() => {
      return {
        px: isMobile && !wrap ? 1.2 : 2,
        py: 1,
        pt: 1.4,
        lineHeight: 1,
        color: buttonColor, //"#232a31"
        textTransform: "none",
        justifyContent: isVertical ? "flex-start" : "center",
        width: isVertical && fullWidth ? "100%" : "auto",

        ".MuiBadge-standard": {
          top: "-5px",
          fontSize: "9px !important",
          right: "-5px",
        },

        // Border positioning based on orientation
        ...(isVertical
          ? {
              borderRight: "2px solid transparent",
              borderRadius: "4px",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }
          : {
              borderBottom: "2px solid transparent",
              borderRadius: "4px",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }),

        ".MuiSvgIcon-root": {
          mr: isVertical ? 1 : -0.6,
          fontSize: "14px !important",
        },

        // Active state uses theme palette tokens:
        //   background  → secondary.activeTabBg  (accent yellow, translucent)
        //   border      → primary.main            (brand blue)
        "&.active": {
          // background: activeTabBg,
          fontWeight: 500,
          ...(isVertical
            ? { borderRight: `2px solid ${activeTabBorder}` }
            : { borderBottom: `2px solid ${activeTabBorder}` }),
        },
      };
    }, [
      isMobile,
      isVertical,
      fullWidth,
      wrap,
      activeTabBg,
      activeTabBorder,
      buttonColor,
    ]);

    const containerStyles = useMemo(() => {
      const baseStyles = {
        gap: 0.2,
        flexWrap: wrap ? "wrap" : "nowrap",
        ...(isVertical
          ? {
              flexDirection: "column",
              width: "100%",
            }
          : {
              // borderBottom: "2px solid #e2e8f0",
              // mb: 1,
              // pb: 0.1,
            }),
      };
      return { ...baseStyles, ...sx };
    }, [isVertical, wrap, sx]);

    return (
      <Flx sx={containerStyles}>
        {tabs.map((tab) => {
          const isActive =
            useRouting && tab.path ? value === tab.path : value === tab.value;
          const displayLabel =
            isMobile && tab.mobileLabel ? tab.mobileLabel : tab.label;

          const buttonContent = tab.badge?.show ? (
            <Badge
              color={tab.badge.color || "error"}
              variant={tab.badge.variant || "dot"}
              badgeContent={
                tab.badge.variant === "standard" ? tab.badge.content : undefined
              }
              invisible={!tab.badge.show}
            >
              {displayLabel}
            </Badge>
          ) : (
            displayLabel
          );

          return (
            <Button
              key={tab.value}
              sx={buttonStyles}
              size="small"
              variant="text"
              className={isActive ? "active" : ""}
              onClick={() => handleTabClick(tab)}
              startIcon={tab.icon}
            >
              {buttonContent}
            </Button>
          );
        })}
      </Flx>
    );
  },
);

EnhancedTabSwitcher.displayName = "EnhancedTabSwitcher";

export default EnhancedTabSwitcher;
